import axios from "axios";
import { toast } from "sonner";

const BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://api.dmflio.com"
);

export const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  // Keep this if you use HttpOnly cookies as well, 
  // otherwise it's optional for pure JWT setups
  withCredentials: true, 
  headers: { "Content-Type": "application/json" },
});

let refreshPromise = null;

function redirectToSessionExpired() {
  sessionStorage.setItem("auth_notice", "session_expired");
  localStorage.removeItem("access_token");
  if (!window.location.pathname.includes("/login")) {
    window.location.assign("/login?error=session_expired");
  }
}

/**
 * 2. REQUEST INTERCEPTOR
 * Automatically attach JWT to every request
 */
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

/**
 * 3. RESPONSE INTERCEPTOR (The Missing Production Piece)
 * Handle token expiration (401) globally
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const url = original.url || "";

    if (error.response?.status === 401 && !original._retry && !url.includes("/auth/login") && !url.includes("/auth/refresh")) {
      original._retry = true;
      try {
        refreshPromise = refreshPromise || api.post("/auth/refresh");
        const { data } = await refreshPromise;
        refreshPromise = null;

        if (data.access_token) {
          localStorage.setItem("access_token", data.access_token);
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${data.access_token}`;
        }
        return api(original);
      } catch (refreshError) {
        refreshPromise = null;
        console.warn("Session expired. Logging out...");
        redirectToSessionExpired();
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 401) {
      console.warn("Session expired. Logging out...");
      toast.error("Your session has expired. Please log in again.");
      redirectToSessionExpired();
    }
    return Promise.reject(error);
  }
);

/**
 * 4. ROBUST ERROR FORMATTER
 * Handles FastAPI/Django/Node error structures
 */
export function formatApiError(detail) {
  if (detail == null) return "An unexpected error occurred.";
  
  // If it's a simple string
  if (typeof detail === "string") {
    const lower = detail.toLowerCase();
    if (lower.includes("token expired") || lower.includes("not authenticated")) {
      return "Your session expired. Please log in again.";
    }
    return detail;
  }

  // If it's a FastAPI validation error list (detail: [{msg: '...', ...}])
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : "Invalid input"))
      .join(". ");
  }

  // If it's a nested object (detail: { msg: '...' })
  if (detail && typeof detail.msg === "string") return detail.msg;

  // Generic fallback
  return "Error communicating with server.";
}
